import { HELD } from "../../hooks/draft/store";
import { heldMessage, scopeLabel } from "../../hooks/draft/labels";

// An edit of the draft of a page, refused (with the reason) when the row
// can't be drafted there:
// - "held": the row has unsaved changes in the draft of another scope. The
//   store keeps a row in one scope at a time and refuses an edit from another
//   scope (which this page neither shows nor saves): the user saves or
//   discards those changes first.
// - "main": nothing is drafted on main (the store notified it).
// - "error": the store refused it (the row of an open Item form, a row not
//   found or removed, a create of a row that exists).
// Pure: the store is passed in (see useScopeEdit for the app's).

// The other dirty scope that journals accessor:id, or null.
export function heldBy(store, scope, accessor, id) {
  const owner = store.ownerOf(accessor, id);
  return owner && owner !== scope ? owner : null;
}

// The stored row of accessor:id: the original of the draft entry that holds
// it, or the row (peek) when no draft holds it. For the updaters that bring
// a row back to the stored one when a change is undone.
export function findStoredRow(store, accessor, id, peek) {
  if (store.ownerOf(accessor, id))
    return store.storedOriginal(accessor, id) ?? undefined;
  return peek(accessor, id);
}

// A boolean field of a draft row: the stored value when it means the same
// (none or 0 for false, 1 for true), so that undoing a change gives the
// stored row back and the entry leaves the draft. Returns the row.
export function setFlag(row, stored, key, value) {
  if (Boolean(stored?.[key]) === value) {
    if (stored && key in stored) row[key] = stored[key];
    else delete row[key];
  } else row[key] = value;

  return row;
}

// A boolean field of a variant of a draft endpoint (by index in the draft
// row), compared with the same variant (same block) of the stored row.
export function setVariantFlag(endpoint, stored, variantIndex, key, value) {
  const variant = endpoint.variants?.[variantIndex];
  if (!variant) throw new Error(`no variant ${variantIndex} in ${endpoint.id}`);

  const storedVariant = stored?.variants?.find(
    (v) => v.blockId === variant.blockId
  );
  setFlag(variant, storedVariant, key, value);
  return endpoint;
}

const refused = (error) =>
  error?.code === HELD
    ? { ok: false, reason: "held", scope: error.scope }
    : { ok: false, reason: "error", error };

// The row an op of draft.change is about.
const targetOf = (op) => ({
  accessor: op?.accessor,
  id: op?.kind === "create" ? op?.row?.id : op?.id
});

// { ok: true, row } | { ok: false, reason: "held", scope } |
// { ok: false, reason: "main" } (the store notified it) |
// { ok: false, reason: "error", error }
export function editInScope(store, scope, accessor, id, updater) {
  const holder = heldBy(store, scope, accessor, id);
  if (holder) return { ok: false, reason: "held", scope: holder };

  try {
    const row = store.edit(scope, accessor, id, updater);
    return row === false ? { ok: false, reason: "main" } : { ok: true, row };
  } catch (error) {
    return refused(error);
  }
}

// Several rows of one operation, all or none (edits: [{ accessor, id,
// updater(row, prior) }]): { ok: true, rows } or a refusal as editInScope,
// with the accessor and id of the row refused.
export function editAllInScope(store, scope, edits) {
  for (const { accessor, id } of edits) {
    const holder = heldBy(store, scope, accessor, id);
    if (holder)
      return { ok: false, reason: "held", scope: holder, accessor, id };
  }

  try {
    const rows = store.editAll(scope, edits);
    return rows === false ? { ok: false, reason: "main" } : { ok: true, rows };
  } catch (error) {
    return refused(error);
  }
}

// The rows of one operation created, edited and removed together
// (draft.change, all or none; ops: [{ kind: "create", accessor, row } |
// { kind: "edit", accessor, id, updater(row, prior) } | { kind: "remove",
// accessor, id, reference }]). options: closing, dryRun (anyScope: the rows
// held by another scope go to that draft). { ok: true, rows } (a Map key ->
// row, null for a remove) or a refusal as editAllInScope, with the accessor
// and id of the row refused (when the store says which).
export function changeInScope(store, scope, ops, options = {}) {
  if (!options.anyScope) {
    for (const op of ops) {
      const { accessor, id } = targetOf(op);
      const holder = heldBy(store, scope, accessor, id);
      if (holder)
        return { ok: false, reason: "held", scope: holder, accessor, id };
    }
  }

  try {
    const rows = store.change(scope, ops, options);
    return rows === false ? { ok: false, reason: "main" } : { ok: true, rows };
  } catch (error) {
    const result = refused(error);
    if (error?.accessor !== undefined) result.accessor = error.accessor;
    if (error?.id !== undefined) result.id = error.id;
    return result;
  }
}

export { scopeLabel };

// The message of a refused edit.
export function refusalMessage(result, { accessor, id, label }) {
  const what = `${label ?? accessor} ${id}`;

  if (result.reason === "held") {
    return heldMessage({ accessor, id, label }, result.scopeLabel);
  }

  const message = String(result.error?.message ?? result.error ?? "");
  if (message.includes("open Item form")) {
    return `${what} is open in a form: change it there.`;
  }
  return `${what} can't be changed: ${message}`;
}
